import { supabase } from '@/lib/supabase'
import type { WidgetConfig, WidgetConfigFormData } from '@/types/widget'
import { v4 as uuidv4 } from 'uuid'

export class WidgetService {
  static async getWidgetConfig(userId: string): Promise<WidgetConfig | null> {
    const { data, error } = await supabase
      .from('widget_configs')
      .select('*')
      .eq('user_id', userId)
      .single()

    if (error && error.code !== 'PGRST116') {
      console.error('Error fetching widget config:', error)
      throw error
    }

    if (!data) return null

    return {
      id: data.id,
      userId: data.user_id,
      placement: data.placement,
      logoUrl: data.logo_url,
      companyName: data.company_name,
      dataSource: data.data_source,
      primaryColor: data.primary_color || undefined,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
    }
  }

  static async createWidgetConfig(
    userId: string,
    config: WidgetConfigFormData
  ): Promise<WidgetConfig> {
    const id = uuidv4()

    const { data, error } = await supabase
      .from('widget_configs')
      .insert({
        id,
        user_id: userId,
        placement: config.placement,
        logo_url: config.logoUrl,
        company_name: config.companyName,
        data_source: config.dataSource,
        primary_color: config.primaryColor,
      })
      .select()
      .single()

    if (error) {
      console.error('Error creating widget config:', error)
      throw error
    }

    return {
      id: data.id,
      userId: data.user_id,
      placement: data.placement,
      logoUrl: data.logo_url,
      companyName: data.company_name,
      dataSource: data.data_source,
      primaryColor: data.primary_color || undefined,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
    }
  }

  static async updateWidgetConfig(
    userId: string,
    config: WidgetConfigFormData
  ): Promise<WidgetConfig> {
    const { data, error } = await supabase
      .from('widget_configs')
      .update({
        placement: config.placement,
        logo_url: config.logoUrl,
        company_name: config.companyName,
        data_source: config.dataSource,
        primary_color: config.primaryColor,
        updated_at: new Date().toISOString(),
      })
      .eq('user_id', userId)
      .select()
      .single()

    if (error) {
      console.error('Error updating widget config:', error)
      throw error
    }

    return {
      id: data.id,
      userId: data.user_id,
      placement: data.placement,
      logoUrl: data.logo_url,
      companyName: data.company_name,
      dataSource: data.data_source,
      primaryColor: data.primary_color || undefined,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
    }
  }

  static async uploadLogo(userId: string, file: File): Promise<string> {
    const fileExt = file.name.split('.').pop()
    const fileName = `${userId}/${uuidv4()}.${fileExt}`

    const { error: uploadError } = await supabase.storage
      .from('widget-logos')
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: false,
      })

    if (uploadError) {
      console.error('Error uploading logo:', uploadError)
      throw uploadError
    }

    const { data } = supabase.storage.from('widget-logos').getPublicUrl(fileName)

    return data.publicUrl
  }

  static async deleteLogo(logoUrl: string): Promise<void> {
    // Extract the file path from the URL
    const urlParts = logoUrl.split('/widget-logos/')
    if (urlParts.length !== 2) return

    const filePath = urlParts[1]

    const { error } = await supabase.storage.from('widget-logos').remove([filePath])

    if (error) {
      console.error('Error deleting logo:', error)
      // Don't throw here, as the file might already be deleted
    }
  }
}
